import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";
import "./Autocomplete.css";

const Autocomplete = ({ onPlaceSelected, onQueryChange }) => {
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [placeDetails, setPlaceDetails] = useState({});
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [isEditing, setIsEditing] = useState(true);
  const [servicesReady, setServicesReady] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const queryRef = useRef("");

  useEffect(() => {
    const initServices = () => {
      if (!window.google?.maps?.places) return false;
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      setServicesReady(true);
      return true;
    };

    if (!initServices()) {
      const interval = setInterval(() => {
        if (initServices()) clearInterval(interval);
      }, 300);
      return () => clearInterval(interval);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setPredictions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchPlaceDetails = useCallback((suggestions) => {
    if (!placesService.current) return;

    suggestions.forEach((suggestion) => {
      placesService.current.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ["name", "formatted_address", "place_id"],
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            setPlaceDetails((prev) => ({
              ...prev,
              [suggestion.place_id]: {
                name: place.name || suggestion.structured_formatting?.main_text || "",
                address: place.formatted_address || "Address unavailable",
              },
            }));
          }
        }
      );
    });
  }, []);

  const requestPredictions = useCallback(
    (value) => {
      if (!value || !autocompleteService.current) {
        setPredictions([]);
        return;
      }

      autocompleteService.current.getPlacePredictions(
        { input: value, types: ["establishment"] },
        (suggestions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && suggestions) {
            setPredictions(suggestions);
            fetchPlaceDetails(suggestions);
          } else {
            setPredictions([]);
          }
        }
      );
    },
    [fetchPlaceDetails]
  );

  // Places services may finish loading after the user has already started typing.
  useEffect(() => {
    if (servicesReady && queryRef.current) {
      requestPredictions(queryRef.current);
    }
  }, [servicesReady, requestPredictions]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    queryRef.current = inputValue;
    setQuery(inputValue);
    setSelectedPlace(null);
    setIsEditing(true);
    onQueryChange?.(inputValue);
    requestPredictions(inputValue);
  };

  const handleSelect = (prediction) => {
    const details = placeDetails[prediction.place_id];
    const name =
      details?.name ||
      prediction.structured_formatting?.main_text ||
      prediction.description;
    const address = details?.address || prediction.structured_formatting?.secondary_text || "";

    const applySelection = (resolvedName, resolvedAddress) => {
      const selected = {
        placeId: prediction.place_id,
        name: resolvedName,
        address: resolvedAddress,
      };
      setSelectedPlace(selected);
      queryRef.current = resolvedName;
      setQuery(resolvedName);
      setPredictions([]);
      setIsEditing(false);
      onPlaceSelected?.(selected);
    };

    if (details?.address) {
      applySelection(name, address);
      return;
    }

    if (!placesService.current) {
      applySelection(name, address);
      return;
    }

    placesService.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["name", "formatted_address", "place_id"],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          applySelection(place.name || name, place.formatted_address || address);
        } else {
          applySelection(name, address);
        }
      }
    );
  };

  const handleSelectedClick = () => {
    setIsEditing(true);
    setSelectedPlace(null);
    onQueryChange?.(query);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const getSuggestionContent = (prediction) => {
    const details = placeDetails[prediction.place_id];
    return {
      name:
        details?.name ||
        prediction.structured_formatting?.main_text ||
        prediction.description,
      address:
        details?.address ||
        prediction.structured_formatting?.secondary_text ||
        "Loading address...",
    };
  };

  return (
    <div className="autocomplete-container" ref={containerRef}>
      {selectedPlace && !isEditing ? (
        <div
          className="selected-place-box"
          onClick={handleSelectedClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleSelectedClick();
          }}
          role="button"
          tabIndex={0}
          aria-label="Edit selected business"
        >
          <span className="selected-place-name">{selectedPlace.name}</span>
          <span className="selected-place-address">
            <FontAwesomeIcon icon={faLocationDot} className="location-pin" />
            <span className="address-text">{selectedPlace.address}</span>
          </span>
        </div>
      ) : (
        <input
          ref={inputRef}
          type="text"
          className="autocomplete-input"
          value={query}
          onChange={handleInputChange}
          placeholder="Enter your business name"
          autoComplete="off"
        />
      )}

      {predictions.length > 0 && isEditing && (
        <ul className="autocomplete-suggestions">
          {predictions.map((prediction) => {
            const { name, address } = getSuggestionContent(prediction);
            return (
              <li
                key={prediction.place_id}
                className="suggestion-item"
                onClick={() => handleSelect(prediction)}
              >
                <div className="suggestion-name">{name}</div>
                <div className="suggestion-address">
                  <FontAwesomeIcon icon={faLocationDot} className="location-pin" />
                  <span>{address}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Autocomplete;
